import useSWR from "swr";
import Link from "next/link";
import { useState, useEffect } from "react";
import type { GetServerSideProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { Spin, Empty } from "antd";
import { getProductsDetailApi } from "@/api/product";
import { ICategory, IAttr, IBrand } from "@/props/IProduct";
import { useRouter } from "next/router";
import ProductImages from "@/components/MallDetail/ProductImages/Index";
import {
  ProductTitle,
  ProductPrice,
  ProductDesc,
  ProductCountry,
  ProductBrand,
  ProductStock,
  ProductNum,
  ProductCategory,
} from "@/components/MallDetail/ProductInfo";
import ProductAttr from "@/components/MallDetail/ProductAttr/Index";
import ProductActionButtons from "@/components/MallDetail/ProductActionButtons";
import ProductContent from "@/components/MallDetail/ProductContent";
import ProductShare from "@/components/MallDetail/ProductShare";
import ProductFavorite from "@/components/MallDetail/ProductFavorite";
import MobilePageHeader from "@/components/Mobile/Header/PageTitle";
import MobileAttrSelect from "@/components/Mobile/MallDetail/AttrSelect";
import MobileAttrPopup from "@/components/Mobile/MallDetail/AttrPopup";
import { isObjectEqual } from "@/utils/index";
import debounce from "lodash/debounce";
import { useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/index";

// 创建 fetcher 函数
const fetcher = async (url: string) => {
  const data = await getProductsDetailApi(url);
  return data;
};

/**
 * 商品详情页
 */
const ProductDetailPage = () => {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const currentLocale: string | undefined =
    router.locale?.toUpperCase() || "EN";
  const [cartNum, setCartNum] = useState<number>(1);

  const [category, setCategory] = useState<ICategory[]>();
  const [tempImages, setTempImages] = useState<string[]>();
  const [images, setImages] = useState<string[]>();
  const [video, setVideo] = useState<string | undefined>(undefined);
  const [countriesData, setCountries] = useState<string[]>();
  const [attrs, setAttrs] = useState<IAttr[]>();
  const [attrSelect, setAttrSelect] = useState<object>({});
  const [attrSelectArr, setAttrSelectArr] = useState<string[]>([]);
  const [attrValues, setAttrValues] = useState<any>({});
  const [specPrice, setSpecPrice] = useState<number>(0);
  const [specStock, setSpecStock] = useState<number>(0);
  const [attrId, setAttrId] = useState<number>();
  const [brand, setBrand] = useState<IBrand>();

  const { isMobile } = useAppSelector((state: RootState) => state.app);
  const [mobileHeaderBgWhite, setMobileHeaderBgWhite] =
    useState<boolean>(false);

  const [isLanguageReady, setIsLanguageReady] = useState(false);

  // 使用 SWR 获取商品数据
  const { data: productData, error } = useSWR<any>(
    isLanguageReady && router.query.id ? String(router.query.id) : null,
    fetcher,
    {
      onSuccess: (data) => {
        const {
          productInfo,
          categories,
          productAttr,
          productValue,
          productBrand,
        } = data;

        setBrand(productBrand ? productBrand : {});
        if (categories) {
          handleTreeCategory(categories);
          setCategory(categories);
        }

        setCountries(productInfo.country.split(","));

        productInfo.storeName = productInfo.storeName
          ? JSON.parse(productInfo.storeName)
          : {};
        productInfo.storeInfo = productInfo.storeInfo
          ? JSON.parse(productInfo.storeInfo)
          : {};
        productInfo.country = productInfo.country
          ? productInfo.country.split(",")
          : undefined;
        productInfo.content = productInfo.content
          ? JSON.parse(productInfo.content)
          : {};
        productInfo.sliderImage = productInfo.sliderImage
          ? JSON.parse(productInfo.sliderImage)
          : {};
        productInfo.image = productInfo.image
          ? JSON.parse(productInfo.image)
          : {};
        productInfo.mainVideoImage = productInfo.mainVideoImage
          ? JSON.parse(productInfo.mainVideoImage)
          : {};

        let imagesData: string[] = [];
        // cover image
        if (productInfo.image[currentLocale]) {
          imagesData.push(productInfo.image[currentLocale]);
        }
        // slider images array
        if (productInfo.sliderImage[currentLocale]) {
          imagesData = imagesData.concat(
            productInfo.sliderImage[currentLocale]
          );
        }
        // video
        if (productInfo.mainVideoImage[currentLocale]) {
          setVideo(productInfo.mainVideoImage[currentLocale]);
        } else {
          setVideo(undefined);
        }

        if (productInfo.specType) {
          setTempImages(Array.from(new Set(imagesData)));

          productAttr.attrValues = JSON.parse(productAttr.attrValues);
          setAttrs(productAttr.attrValues[currentLocale]);

          for (let k in productValue) {
            productValue[k]["attrValue"] = JSON.parse(
              productValue[k]["attrValue"]
            )[currentLocale];
            for (let kk in productValue[k]["attrValue"]) {
              productValue[k][kk] = productValue[k]["attrValue"][kk];
            }
          }

          setAttrValues(productValue);

          let attrSelectObj: any = {};
          productAttr.attrValues[currentLocale].map((attr: IAttr) => {
            attr.attrValue.forEach((attrValue, index) => {
              if (index === 0) {
                attrSelectObj[attr.attrName] = "";
              }
            });
          });

          setAttrSelect(attrSelectObj);
        } else {
          if (productValue.default && productValue.default.id) {
            setAttrId(productValue.default.id);
          }
          if (
            productValue.default &&
            productValue.default.image[currentLocale]
          ) {
            imagesData.push(productValue.default.image[currentLocale]);
          }
          setImages(Array.from(new Set(imagesData)));
        }
      },
      onError: (error) => {
        console.log("Failed:", error);
      },
    }
  );

  const product = productData?.productInfo;

  const handleTreeCategory = (categories: ICategory[]) => {
    categories.forEach((item: ICategory) => {
      item.name = item.name ? JSON.parse(item.name) : {};
      if (item.child) {
        handleTreeCategory(item.child);
      }
    });
  };

  /**
   * 计算多规格商品价格
   */
  const computedSpecPrice = () => {
    for (let selectK in attrSelect) {
      if (!attrSelect[selectK]) {
        setSpecPrice(product?.price || 0);
        setSpecStock(product?.stock || 0);
        setAttrId(0);
        setImages(Array.from(new Set(JSON.parse(JSON.stringify(tempImages)))));
        return;
      }
    }

    for (let k in attrValues) {
      const attrValue = attrValues[k];
      // 根据已选择的规格值，选中规格组合商品信息
      if (isObjectEqual(attrSelect, attrValue.attrValue)) {
        setSpecPrice(attrValue["price"]);
        setSpecStock(attrValue["stock"]);
        setAttrId(attrValue["id"]);
        const tempImagesData = JSON.parse(JSON.stringify(tempImages));
        if (
          attrValue["image"] &&
          JSON.parse(attrValue["image"])[currentLocale]
        ) {
          tempImagesData.push(JSON.parse(attrValue["image"])[currentLocale]);
        }

        setImages(Array.from(new Set(tempImagesData)));
      }
    }
  };

  /**
   * 获取当前规格库存
   * @param attrName 规格名称
   * @param attrValue 规格值
   * @returns 当前规格值和已选中规格商品的库存
   */
  const getCurSpecStock = (attrName, attrValue) => {
    const tempAttrSelect = JSON.parse(JSON.stringify(attrSelect));
    tempAttrSelect[attrName] = attrValue;

    // 所有不为空的规格值
    const tempAttrSelectValues = Object.values(tempAttrSelect).filter(
      (value) => value
    );
    // 当有规格值为空时，不进行处理
    if (tempAttrSelectValues.length < Object.keys(tempAttrSelect).length) {
      return;
    }

    let curAttr = undefined;

    for (let k in attrValues) {
      // 根据已选择的规格值，选中规格组合商品信息
      if (isObjectEqual(tempAttrSelect, attrValues[k].attrValue)) {
        curAttr = attrValues[k];
        return attrValues[k]["stock"] || 0;
      }
    }

    // 规格不存在
    if (!curAttr) return 0;

    // 规格存在，返回商品库存
    return curAttr["stock"] || 0;
  };

  /**
   * 规格改变 event
   * @param attrName 规格名称
   * @param spec 规格值
   */
  const onSpecChange = (attrName: string, spec: string) => {
    const tempData = JSON.parse(JSON.stringify(attrSelect));
    attrSelect[attrName] === spec
      ? (tempData[attrName] = "")
      : (tempData[attrName] = spec);
    setAttrSelect(tempData);
  };

  useEffect(() => {
    const updateLanguageAndFetchData = async () => {
      if (router.locale && i18n.language !== router.locale) {
        await i18n.changeLanguage(router.locale);
        router.reload();
      }
      setIsLanguageReady(true);
    };
    updateLanguageAndFetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.locale, i18n, router.query]);

  useEffect(() => {
    if (attrValues && Object.keys(attrValues).length > 0) {
      computedSpecPrice();
    }
    setAttrSelectArr(Object.values(attrSelect).filter((item) => item));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attrSelect]);

  // handle scroll event
  const handleScroll = () => {
    if (!isMobile) return;
    const scrollTop = document.documentElement.scrollTop;
    setMobileHeaderBgWhite(scrollTop >= 320);
  };

  useEffect(() => {
    // use debounce
    const debouncedScrollHandler = debounce(handleScroll, 200);
    window.addEventListener("scroll", debouncedScrollHandler);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Spin spinning={!isLanguageReady || (!productData && !error)}>
      <div className="flex min-h-screen w-screen flex-col lg:w-md-container xl:w-container">
        {/* 面包屑：首页 | 类别层级... | 商品名称 */}
        <div className="my-[42px] hidden items-center text-base font-semibold lg:flex">
          <Link href="/" className="shrink-0 text-stone-100 hover:text-primary hover:underline transition-colors duration-200">
            {t("menu.home")}
          </Link>
          {category && category.length > 0 &&
            (() => {
              // 取第一条分类链，展平为 [一级, 二级, 三级...]
              const flatPath: ICategory[] = [];
              const flatten = (node: ICategory) => {
                flatPath.push(node);
                if (node.child && node.child.length > 0) {
                  flatten(node.child[0]);
                }
              };
              flatten(category[0]);
              return flatPath.map((cat) => (
                <div className="flex items-center" key={cat.id}>
                  <div className="mx-4 h-[19px] w-0.5 bg-stone-100" />
                  <Link
                    href={{ pathname: "/mall", query: { cid: cat.id } }}
                    locale={router.locale}
                    className="shrink-0 text-stone-100 hover:text-primary hover:underline transition-colors duration-200"
                  >
                    {cat.name?.[currentLocale] || ""}
                  </Link>
                </div>
              ));
            })()}
          {product && (
            <>
              <div className="mx-4 h-[19px] w-0.5 bg-stone-100" />
              <span className="truncate text-primary">{product.storeName[currentLocale]}</span>
            </>
          )}
        </div>

        <MobilePageHeader
          back={true}
          transparent={!mobileHeaderBgWhite}
          fixed={true}
          right={
            product ? (
              <div className="flex items-center">
                <ProductFavorite product={product} isMobile />
                <ProductShare
                  product={{
                    id: product.id,
                    image: product.image,
                    storeName: product.storeName,
                    price: product.price,
                  }}
                  isMobile
                />
              </div>
            ) : null
          }
        />

        {product ? (
          <>
            {/* product images & product info */}
            <div className="mb-10 flex w-full flex-col justify-between lg:mb-20 lg:flex-row">
              <div className="mr-0 flex w-full flex-col lg:mr-5 lg:w-[470px] xl:mr-[30px]">
                {images ? (
                  <ProductImages imagesData={images} videoUrl={video} />
                ) : (
                  <ProductImages loading={true} imagesData={[]} videoUrl={""} />
                )}
                <ProductShare
                  product={{
                    id: product.id,
                    image: product.image,
                    storeName: product.storeName,
                    price: product.price,
                  }}
                />
              </div>

              <div className="mt-[27px] flex flex-1 flex-col px-mobile text-sm text-stone-800  lg:mt-0 lg:px-0">
                <ProductTitle title={product.storeName[currentLocale]} />
                <ProductPrice
                  price={!product.specType ? product.price : specPrice}
                />
                <ProductDesc desc={product.storeInfo[currentLocale]} />

                {countriesData && <ProductCountry country={countriesData} />}

                {brand && (
                  <ProductBrand
                    brand={
                      currentLocale === "SC" ? brand["nameCn"] : brand["nameEn"]
                    }
                  />
                )}

                <ProductStock
                  stock={!product.specType ? product.stock : specStock}
                />

                <ProductNum
                  value={cartNum}
                  max={!product.specType ? product.stock : specStock}
                  updateCartNum={(cartNum) => {
                    setCartNum(cartNum);
                  }}
                />

                {product.specType && attrs && (
                  <>
                    {/* pc product specification */}
                    <div className="hidden lg:block">
                      <ProductAttr
                        attrs={attrs}
                        attrSelect={attrSelect}
                        getCurSpecStock={getCurSpecStock}
                        onSpecChange={onSpecChange}
                      />
                    </div>

                    {/*  mobile product specification select */}
                    <MobileAttrSelect
                      attrsLen={attrs.length}
                      attrSelectArr={attrSelectArr}
                    />
                  </>
                )}

                <ProductActionButtons
                  product={product}
                  specStock={specStock}
                  specPrice={specPrice}
                  attrId={attrId}
                  cartNum={cartNum}
                />

                {category && (
                  <ProductCategory
                    categories={category}
                    currentLocale={currentLocale}
                  />
                )}
              </div>
            </div>

            {/* product detail */}
            <ProductContent
              title={t("product_detail.product_detail")}
              content={product?.content[currentLocale]}
            />
          </>
        ) : (
          <div className="mt-20">
            <Empty description={t("common.no_data")}></Empty>
          </div>
        )}
      </div>

      <MobileAttrPopup
        product={product}
        specStock={specStock}
        specPrice={specPrice}
        attrId={attrId}
        cartNum={cartNum}
        stock={!product?.specType ? product?.stock || 0 : specStock}
        attrs={attrs || []}
        attrSelect={attrSelect}
        attrSelectArr={attrSelectArr}
        updateCartNum={(cartNum) => {
          setCartNum(cartNum);
        }}
        getCurSpecStock={getCurSpecStock}
        onSpecChange={onSpecChange}
      />
    </Spin>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  locale,
  req,
}) => {
  const resolvedLocale = req.cookies.NEXT_LOCALE || locale || "sc";
  return {
    props: {
      ...(await serverSideTranslations(resolvedLocale, [
        process.env.NEXT_PUBLIC_MTS_NS || "",
      ])),
    },
  };
};

export default ProductDetailPage;
